import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  CardTabs,
  Tab,
  TabList,
  TabPanel,
  TabPanelList,
  Text,
  Toast,
  Tooltip,
} from '@modusign/design-system';
import { createLabel } from '@modusign/openapi-client-category';
import { CreateLabelRequestDtoRole } from '@modusign/openapi-client-category/generated/model';
import { useState } from 'react';
import { useMutation, UseMutationOptions } from 'react-query';
import { useSelector } from 'react-redux';

import {
  ResourceModal,
  ResourceModalBody,
  ResourceModalFooter,
  ResourceModalHeader,
  ResourceModalHeaderWithBackButton,
} from '@app/components/Modal/ResourceModal';
import { ResourceRoleSelectList, ResourceRoleSubject } from '@app/features/authorization';
import { useInvalidateLabelList } from '@app/features/label/hooks/useInvalidateLabelList';
import useOpenErrorModal from '@app/hooks/useOpenErrorModal';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import { useCurrentUserMemberId } from '@app/hooks/workspace';
import pushAdobeDataLayer from '@app/libs/analytics/adobe';
import pushDataLayer from '@app/libs/analytics/googleTagManager';
import { getUserEmail } from '@app/store/user/selectors';
import { getName } from '@app/store/user/user/selectors';

import useLabelBasicInfoForm from '../../hooks/useLabelBasicInfoForm';
import useLabelResourceRoleSet from '../../hooks/useLabelResourceRoleSet';
import validateName from '../../libs/validateName';
import { LabelResourceRoleSubject } from '../../types';
import { ChangeToPrivateLabelButton } from '../ChangeToPrivateLabelButton';
import { ChangeToPublicLabelButton } from '../ChangeToPublicLabelButton';
import LabelBasicInfoForm from '../LabelBasicInfoForm';
import LabelViewerTabPanel from '../LabelViewerTabPanel';
import useLabelCreateModalState, { LabelCreateModalState } from './useCreateLabelModalState';

function getTab(state: LabelCreateModalState): 'BASIC_INFO_FORM' | 'LIST_VIEWERS' {
  if (state.type === 'PUBLIC_LABEL') {
    return 'BASIC_INFO_FORM';
  }

  if (state.content === 'ADD_VIEWERS') {
    throw new Error('inaccessible branch: ADD_VIEWERS state does not have tabs');
  }

  return state.content;
}

interface LabelCreationModalProps {
  onCancel: () => void;
  onCreated: () => void;
}

export default function LabelCreationModal(props: LabelCreationModalProps) {
  return (
    <ResourceModal isOpen maxWidth="sm">
      <LabelCreationModalContent {...props} />
    </ResourceModal>
  );
}

function LabelCreationModalContent({
  onCancel,
  onCreated,
}: Omit<LabelCreationModalProps, 'resourceType'>) {
  const workspaceId = useWorkspaceId();
  const { createErrorHandler } = useOpenErrorModal();

  const userMemberId = useCurrentUserMemberId();
  const userEmail = useSelector(getUserEmail);
  const userName = useSelector(getName);
  const currentUserAsMember: ResourceRoleSubject = {
    subjectType: 'MEMBER',
    subjectId: userMemberId,
    memberEmail: userEmail,
    memberName: userName || '',
  };

  const invalidateLabels = useInvalidateLabelList();

  const { state, changePrivateLabelView, changeToPrivateLabel } = useLabelCreateModalState();
  const { description, isDuplicated, name, nameErrorMessage, onDescriptionChange, onNameChange } =
    useLabelBasicInfoForm();

  const {
    roles,
    add: addRoles,
    remove: removeRoles,
    set: setRoles,
  } = useLabelResourceRoleSet([{ subjectType: 'WORKSPACE', subjectId: workspaceId }]);
  const { mutate: create, isLoading } = useCreateLabel({
    onSuccess: () => {
      Toast.success({ description: '새 라벨을 만들었습니다.' });
      invalidateLabels();
      onCreated();
    },
    onError: createErrorHandler({ title: '라벨 만들기 실패' }),
  });

  if (state.type === 'PRIVATE_LABEL' && state.content === 'ADD_VIEWERS') {
    return (
      <LabelAddResourceRoleModalContent
        onClose={() => changePrivateLabelView('LIST_VIEWERS')}
        onSelect={(value) => {
          addRoles(value);
          changePrivateLabelView('LIST_VIEWERS');
        }}
      />
    );
  }

  const showTab = state.type === 'PRIVATE_LABEL';
  const isPublicLabel = roles.some((r) => r.subjectType === 'WORKSPACE');

  return (
    <>
      <ResourceModalHeader resourceName="" title="새 라벨" onClose={onCancel} />
      <ResourceModalBody>
        <CardTabs
          index={getTab(state) === 'BASIC_INFO_FORM' ? 0 : 1}
          isFitted
          onChange={(index) => {
            changePrivateLabelView(index === 0 ? 'BASIC_INFO_FORM' : 'LIST_VIEWERS');
          }}
        >
          <TabList display={showTab ? 'flex' : 'none'}>
            <Tab>기본 정보</Tab>
            <Tab>
              볼 수 있는 멤버{' '}
              <Tooltip
                content={
                  <Text as="p">
                    라벨 보기·수정·삭제가 가능하며
                    <br />
                    문서·계약 등에 적용할 수 있습니다.
                  </Text>
                }
              >
                <Box
                  color="textNeutralLight"
                  display="inline-block"
                  fontSize="14px"
                  ml="2px"
                  p="3px"
                >
                  <FontAwesomeIcon icon={faQuestionCircle} />
                </Box>
              </Tooltip>
            </Tab>
          </TabList>
          <TabPanelList>
            <TabPanel>
              <Box pt={showTab ? 8 : null}>
                <LabelBasicInfoForm
                  description={description}
                  isDuplicated={isDuplicated}
                  name={name}
                  nameErrorMessage={nameErrorMessage}
                  onDescriptionChange={onDescriptionChange}
                  onNameChange={onNameChange}
                />
              </Box>
            </TabPanel>
            <TabPanel>
              <Box mt={8}>
                <LabelViewerTabPanel
                  roles={roles}
                  onAddClick={() => changePrivateLabelView('ADD_VIEWERS')}
                  onRemove={removeRoles}
                />
              </Box>
            </TabPanel>
          </TabPanelList>
        </CardTabs>
      </ResourceModalBody>
      <ResourceModalFooter alignItems="center">
        {isPublicLabel ? (
          <ChangeToPrivateLabelButton
            onConfirm={() => {
              pushAdobeDataLayer({
                event: 'clickViewRestrictButtonInCreateLabelModal',
              });
              setRoles([currentUserAsMember]);
              changeToPrivateLabel();
            }}
          />
        ) : (
          <ChangeToPublicLabelButton
            needConfirm
            onConfirm={() => {
              setRoles([
                {
                  subjectType: 'WORKSPACE',
                  subjectId: workspaceId,
                },
              ]);
            }}
          />
        )}
        <Box flex="auto" />
        <Button disabled={isLoading} variant="subtle" weight="light" onClick={onCancel}>
          취소
        </Button>
        <Button
          isLoading={isLoading}
          ml={3}
          variant="primary"
          weight="light"
          onClick={() => {
            // 입력하지 않고 라벨 생성 시도했을 때 input 밑에 유효성 안내를 하기 위한 validation trigger
            if (!name) {
              onNameChange(name);
            }
            switch (validateName(name.trim())) {
              case 'EMPTY':
                Toast.warning({ description: '라벨 이름을 입력해 주세요.' });
                changePrivateLabelView('BASIC_INFO_FORM');
                return;
              case 'INVALID':
                Toast.warning({ description: '올바른 형식의 라벨 이름을 입력해 주세요.' });
                changePrivateLabelView('BASIC_INFO_FORM');
                return;
              default:
            }

            create({
              type: state.type,
              name: name.trim(),
              description,
              roles,
            });
          }}
        >
          만들기
        </Button>
      </ResourceModalFooter>
    </>
  );
}

function LabelAddResourceRoleModalContent({
  onSelect,
  onClose,
}: {
  onSelect: (params: ResourceRoleSubject[]) => void;
  onClose: () => void;
}) {
  const [selected, setSelected] = useState<ResourceRoleSubject[]>([]);

  return (
    <>
      <ResourceModalHeaderWithBackButton
        resourceName="라벨을 볼 수 있는 멤버나 그룹을 추가해 보세요."
        title="새 라벨"
        onBack={() => onSelect([])}
        onClose={onClose}
      />
      <ResourceModalBody>
        <ResourceRoleSelectList value={selected} onChange={setSelected} />
      </ResourceModalBody>
      <ResourceModalFooter justifyContent="flex-end">
        <Button variant="subtle" onClick={() => onSelect([])}>
          취소
        </Button>
        <Button
          disabled={!selected.length}
          ml={3}
          variant="primary"
          onClick={() => onSelect(selected)}
        >
          추가
        </Button>
      </ResourceModalFooter>
    </>
  );
}

type UseCreateLabelVariable = {
  name: string;
  description: string;
} & (
  | {
      type: 'PUBLIC_LABEL';
    }
  | {
      type: 'PRIVATE_LABEL';
      roles: LabelResourceRoleSubject[];
    }
);

function useCreateLabel(options?: UseMutationOptions<unknown, Error, UseCreateLabelVariable>) {
  const workspaceId = useWorkspaceId();

  return useMutation<unknown, Error, UseCreateLabelVariable>(
    (params) => {
      let rolesDto: CreateLabelRequestDtoRole[];
      if (params.type === 'PUBLIC_LABEL') {
        rolesDto = [{ name: 'EDITOR', subjectId: workspaceId, subjectType: 'WORKSPACE' }];
      } else {
        rolesDto = params.roles.map(({ subjectId, subjectType }) => ({
          subjectType,
          subjectId,
          name: 'EDITOR',
        }));
      }

      return createLabel(workspaceId, {
        roles: rolesDto,
        name: params.name,
        description: params.description,
      });
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        options?.onSuccess?.(data, variables, context);
        pushDataLayer({
          event: 'createLabel',
          labelType: variables.type,
        });
      },
    }
  );
}
